'use client'

import { type UrlObject } from 'url'
import { PlayIcon } from '@hedepy/icons/refactor/Play'
import Image from 'next/image'
import Link from 'next/link'
import React, { useState } from 'react'
import { useIsomorphicLayoutEffect } from 'swr/_internal'

import { cn } from '@/legacy-src/lib/utils'

import { ButtonIcon } from '@/src/domains/design-system/components/Button'

type Props = {
  source?: string
  identifier: string
  openModal?: null | (() => void)
  href?: UrlObject | string
  className?: string
  width?: number
  height?: number
  prefetch?: boolean
}

const TherapistProfileImage: React.FC<Props> = ({
  source,
  identifier,
  openModal = null,
  href,
  className = '',
  width = 80,
  height = 80,
  prefetch,
}) => {
  const [imageUrl, setImageUrl] = useState(source)

  useIsomorphicLayoutEffect(() => {
    setImageUrl(source)
  }, [source])

  const fallbackUrl = '/images/svg/misc/therapist-placeholder.svg'

  const src = imageUrl || fallbackUrl

  const renderImage = () => (
    <Image
      key={src}
      src={src}
      alt={identifier}
      fill
      sizes={`${width}px`}
      onError={() => {
        setImageUrl(fallbackUrl)
      }}
      className="overflow-hidden"
      style={{
        maskImage: "url('/images/ellipsis-rebrand.svg')",
        maskPosition: 'center',
        maskRepeat: 'no-repeat',
        objectFit: 'cover',
        overflowClipMargin: 'unset',
      }}
    />
  )

  return (
    <div className={cn('relative', className)} style={{ height, width }}>
      {href ? <Link {...{ href, prefetch }}>{renderImage()}</Link> : renderImage()}
      {typeof openModal === 'function' && (
        <ButtonIcon
          size="small"
          className="absolute bottom-0 right-0 h-[2rem] min-h-[2rem] w-[2rem] rounded-lg"
          onClick={openModal}
        >
          <PlayIcon fill="currentColor" />
        </ButtonIcon>
      )}
    </div>
  )
}

export default TherapistProfileImage
